import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import Carousel, { ResponsiveType } from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import chevronLeft from '../../../../assets/chevronLeftBlack.svg';
import chevronRight from '../../../../assets/chevronRightBlack.svg';
import { ErrorType, recordError } from '../../../../models/apiWrappers/api/exceptions';
import { GuidesSection as GuidesSectionData } from '@7chairs/types';
import { useAnalytics } from '../../../../utils/analytics';
import { isDefined } from '../../../../utils/isDefined';
import { GuideCard } from './GuideCard';
import {
  BREAKPOINTS,
  GuidesSectionSliderArrow,
  GuidesSectionSliderArrowImg,
  GuidesSectionSliderArrowsWrapper,
  GuidesSectionSliderWrapper,
  GuidesSectionSubtitle,
  GuidesSectionTitle,
  GuidesSectionTitlesWrapper,
} from './GuidesSection.style';

interface GuidesSectionProps {
  data: GuidesSectionData;
}

interface AudioState {
  audio: HTMLAudioElement;
  isPlaying: boolean;
}

const carouselResponsiveSettings: ResponsiveType = {
  desktopBig: {
    breakpoint: { max: BREAKPOINTS.DESKTOP_BIG_MAX, min: BREAKPOINTS.DESKTOP_BIG_MIN },
    items: 4,
    partialVisibilityGutter: 15,
  },
  desktopMedium: {
    breakpoint: { max: BREAKPOINTS.DESKTOP_MEDIUM_MAX, min: BREAKPOINTS.DESKTOP_MEDIUM_MIN },
    items: 3,
    partialVisibilityGutter: 20,
  },
  desktopToTablet: {
    breakpoint: { max: BREAKPOINTS.DESKTOP_TO_TABLET_MAX, min: BREAKPOINTS.DESKTOP_TO_TABLET_MIN },
    items: 2,
    partialVisibilityGutter: 30,
  },
  mobile: {
    breakpoint: { max: BREAKPOINTS.MOBILE_MAX, min: BREAKPOINTS.MOBILE_MIN },
    items: 1,
    partialVisibilityGutter: 60,
  },
};

export const GuidesSection = (props: GuidesSectionProps) => {
  const { data } = props;

  const analytics = useAnalytics();

  const sliderRef = useRef<null | Carousel>(null);
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState<number | null>(null);
  const [audioMap, setAudioMap] = useState<Map<number, AudioState>>(() => {
    const map = new Map<number, AudioState>();

    data.guides.forEach((guide, index) => {
      map.set(index, {
        audio: new Audio(guide.defaultAudioUrl),
        isPlaying: false,
      });
    });

    return map;
  });

  const handleRef = (slider: null | Carousel) => {
    if (slider) {
      sliderRef.current = slider;
    }
  };

  const nextGuide = () => {
    const currentSlideIndex = sliderRef.current?.state.currentSlide;

    if (isDefined(currentSlideIndex)) {
      const nextSlide = currentSlideIndex + 1;
      sliderRef.current?.goToSlide(nextSlide);

      analytics.track('Web paywall walkthrough professional led click', {
        direction: 'next',
      });
    }
  };
  const previousGuide = () => {
    const currentSlideIndex = sliderRef.current?.state.currentSlide;

    if (isDefined(currentSlideIndex)) {
      const nextSlide = currentSlideIndex - 1;
      sliderRef.current?.goToSlide(nextSlide);
      analytics.track('Web paywall walkthrough professional led click', {
        direction: 'previous',
      });
    }
  };

  const turnOffCurrentlyPlayingAudio = useCallback(() => {
    if (isDefined(currentlyPlayingIndex)) {
      const audioState = audioMap.get(currentlyPlayingIndex);
      if (!audioState) {
        recordError(ErrorType.HANDLED, '[turnOffCurrentlyPlayingAudio] Audio state not found!', {
          index: currentlyPlayingIndex,
        });
        return;
      }
      const newAudioMap = new Map(audioMap);
      const state = audioMap.get(currentlyPlayingIndex);
      if (!state) {
        recordError(ErrorType.HANDLED, '[turnOffCurrentlyPlayingAudio] State not found!', { currentlyPlayingIndex });
        return;
      }
      if (!state.isPlaying) {
        return;
      }
      state.audio.load();
      newAudioMap.set(currentlyPlayingIndex, { ...state, isPlaying: false });
      setAudioMap(newAudioMap);
      setCurrentlyPlayingIndex(null);
    }
  }, [currentlyPlayingIndex, audioMap]);

  const onPlayButtonClick = (index: number) => {
    const newAudioMap = new Map(audioMap);

    if (isDefined(currentlyPlayingIndex) && currentlyPlayingIndex !== index) {
      const currentlyPlayingState = audioMap.get(currentlyPlayingIndex);
      if (!currentlyPlayingState) {
        recordError(ErrorType.HANDLED, 'Currently playing state not found!', { index });
        return;
      }

      currentlyPlayingState.audio.pause();
      currentlyPlayingState.audio.currentTime = 0;
      currentlyPlayingState.isPlaying = false;
      newAudioMap.set(currentlyPlayingIndex, currentlyPlayingState);
    }

    const state = audioMap.get(index);
    if (!state) {
      recordError(ErrorType.HANDLED, 'State not found!', { index });
      return;
    }

    if (state.isPlaying) {
      state.audio.pause();
    } else {
      state.audio.play();
    }
    newAudioMap.set(index, { ...state, isPlaying: !state.isPlaying });
    setAudioMap(newAudioMap);
    setCurrentlyPlayingIndex(state.isPlaying ? null : index);
    analytics.track('Web paywall walkthrough professional led audio play', {
      action: state.isPlaying ? 'Pause' : 'Play',
      GFName: data.guides.at(index)?.name,
    });
  };

  const afterCarouselChange = () => {
    if (isMobile) {
      analytics.track('web paywall walkthrough professional led swipe');
    }
    turnOffCurrentlyPlayingAudio();
  };

  useEffect(() => {
    return () => {
      // turn off the audio on component unmount
      turnOffCurrentlyPlayingAudio();
    };
  }, [turnOffCurrentlyPlayingAudio]);

  return (
    <>
      <GuidesSectionTitlesWrapper>
        <GuidesSectionTitle>{data.title}</GuidesSectionTitle>
        <GuidesSectionSubtitle>{data.subtitle}</GuidesSectionSubtitle>
      </GuidesSectionTitlesWrapper>
      <GuidesSectionSliderWrapper>
        <Carousel
          responsive={carouselResponsiveSettings}
          infinite={true}
          arrows={false}
          centerMode={true}
          ref={handleRef}
          afterChange={afterCarouselChange}
        >
          {data.guides.map((guide, index) => (
            <GuideCard
              key={index}
              data={guide}
              index={index}
              isPlaying={audioMap.get(index)?.isPlaying}
              onButtonClick={onPlayButtonClick}
            />
          ))}
        </Carousel>
        <GuidesSectionSliderArrowsWrapper>
          <GuidesSectionSliderArrow onClick={previousGuide}>
            <GuidesSectionSliderArrowImg src={chevronLeft} />
          </GuidesSectionSliderArrow>
          <GuidesSectionSliderArrow onClick={nextGuide}>
            <GuidesSectionSliderArrowImg src={chevronRight} />
          </GuidesSectionSliderArrow>
        </GuidesSectionSliderArrowsWrapper>
      </GuidesSectionSliderWrapper>
    </>
  );
};
